import { useState } from 'react'
import AclEntity from '@models/AclEntity'
import AclIdentity from '@models/AclIdentity'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import IdChip from '@components/atoms/IdChip'
import FlatFeeQuote from '@components/molecules/FlatFeeQuote'
import HourlyFeeQuote from '@components/molecules/HourlyFeeQuote'
import ItemBoundingBox from '@components/atoms/ItemBoundingBox'
import ListItemBoxHeading from '@components/molecules/ListItemBoxHeading'
import { DateFormatter } from '@utils/dateUtils'
import {
  LegalMatterQuoteFragment,
  Permission,
  QuoteItemType,
  QuoteStatus,
} from '@generatedTypes/graphql'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { toDollars, formattedAmount } from '@/utils/stripeUtils'
import log from 'loglevel'
import Grid from '@mui/material/Grid'

const StatusPill = ({
  status,
  short = false,
}: {
  status: QuoteStatus
  short?: boolean
}) => {
  const statusColorMap = {
    [QuoteStatus.Draft]: 'default',
    [QuoteStatus.Sent]: 'warning',
    [QuoteStatus.AcceptedPaymentFailed]: 'error',
    [QuoteStatus.AcceptedPaymentPending]: 'warning',
    [QuoteStatus.AcceptedPaid]: 'success',
    [QuoteStatus.Declined]: 'error',
  }

  const shortStatusLabels = {
    [QuoteStatus.Draft]: 'D',
    [QuoteStatus.Sent]: 'S',
    [QuoteStatus.AcceptedPaymentFailed]: 'A—PF',
    [QuoteStatus.AcceptedPaymentPending]: 'A—PEN',
    [QuoteStatus.AcceptedPaid]: 'A—P',
    [QuoteStatus.Declined]: 'D',
  }

  const statusLabels = {
    [QuoteStatus.Draft]: 'DRAFT',
    [QuoteStatus.Sent]: 'SENT',
    [QuoteStatus.AcceptedPaymentFailed]: 'ACCEPTED—PAYMENT—FAILED',
    [QuoteStatus.AcceptedPaymentPending]: 'ACCEPTED—PAYMENT—PENDING',
    [QuoteStatus.AcceptedPaid]: 'ACCEPTED—PAID',
    [QuoteStatus.Declined]: 'DECLINED',
  }

  return (
    <Chip
      label={short ? shortStatusLabels[status] : statusLabels[status]}
      size="small"
      color={statusColorMap[status] || 'default'}
      variant="filled"
      sx={{ fontWeight: 'bold', padding: '2px 8px', marginLeft: '1rem' }}
    />
  )
}

const QuoteStatusMessage = ({ status }: { status: QuoteStatus }) => {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
      <Typography variant="h3" color="textSecondary">
        Status:
      </Typography>
      <StatusPill status={status} />
      <Typography variant="body1" color="textSecondary">
        {status === QuoteStatus.Draft &&
          "This quote hasn't been sent to the member yet."}
        {status === QuoteStatus.Sent &&
          'The quote is waiting acceptance from the member.'}
        {status === QuoteStatus.Declined &&
          'The quote was declined by the member.'}
        {status === QuoteStatus.AcceptedPaymentPending &&
          'The quote was accepted by the member, payment is pending.'}
        {status === QuoteStatus.AcceptedPaymentFailed &&
          'The quote was accepted by the member but the payment has failed.'}
        {status === QuoteStatus.AcceptedPaid &&
          'The quote was accepted by the member, the payment is complete.'}
      </Typography>
    </Stack>
  )
}

interface QuoteListItemProps {
  currentUserAclIdentities?: AclIdentity[]
  readOnly?: boolean
  quote: LegalMatterQuoteFragment
  inEditMode?: boolean
  editForm?: JSX.Element
  isSending?: boolean
  onClickAccept?: (quoteKey: string) => void
  onClickReject?: (quoteKey: string) => void
  onClickSend?: (quoteKey: string) => void
  onClickMenu?: (event: React.MouseEvent) => void
}

const QuoteListItem = ({
  currentUserAclIdentities = [],
  readOnly = false,
  quote,
  inEditMode = false,
  editForm,
  isSending = false,
  onClickAccept,
  onClickReject,
  onClickSend,
  onClickMenu,
}: QuoteListItemProps) => {
  const [expanded, setExpanded] = useState(false)
  const formattedUpdateTime = quote.lastUpdatedTimestamp
    ? DateFormatter(quote.lastUpdatedTimestamp)
    : 'N/A'
  const quoteItem = quote.items[0]

  let discountInUSD = 0
  if (quoteItem?.discount && quoteItem.payableAmount) {
    discountInUSD = toDollars(quoteItem?.discount * quoteItem.payableAmount)
  }
  if (quoteItem?.discount && quoteItem.hourlyRate) {
    discountInUSD = toDollars(quoteItem?.discount * quoteItem.hourlyRate)
  }

  const finalTotal =
    quoteItem.type === QuoteItemType.Hourly
      ? toDollars(quoteItem.payableAmount)
      : toDollars(quoteItem.payableAmount) -
        discountInUSD +
        toDollars(
          quoteItem?.filingAndOtherFees ? quoteItem?.filingAndOtherFees : 0,
        )

  const quoteAcl = new AclEntity(quote.acl)
  const actions = (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      justifyContent="flex-end"
      sx={{ mt: 2 }}
    >
      {quote.status === QuoteStatus.Draft &&
        onClickSend &&
        quoteAcl.allows(currentUserAclIdentities, Permission.QuotesSend) && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => onClickSend(quote.key)}
            loading={isSending}
          >
            Send Quote
          </Button>
        )}
      {quote.status === QuoteStatus.Sent &&
        onClickReject &&
        quoteAcl.allows(currentUserAclIdentities, Permission.QuotesReject) && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => onClickReject(quote.key)}
            loading={isSending}
          >
            Decline Quote
          </Button>
        )}
      {quote.status === QuoteStatus.Sent &&
        onClickAccept &&
        quoteAcl.allows(currentUserAclIdentities, Permission.QuotesAccept) && (
          <Button
            variant="outlined"
            color="success"
            onClick={() => onClickAccept(quote.key)}
            loading={isSending}
          >
            Accept Quote
          </Button>
        )}
      {quote.status === QuoteStatus.AcceptedPaymentPending &&
        onClickAccept &&
        quoteAcl.allows(currentUserAclIdentities, Permission.QuotesAccept) && (
          <Button
            variant="outlined"
            color="success"
            onClick={() => onClickAccept(quote.key)}
            loading={isSending}
          >
            Make Payment
          </Button>
        )}
    </Stack>
  )

  if (inEditMode) {
    return <>{editForm}</>
  }

  return (
    <ItemBoundingBox variant="outlineBox" data-testid="quote-item">
      <ListItemBoxHeading
        title={
          <Button
            onClick={() => setExpanded(!expanded)}
            color="primary"
            data-testid="toggleButton"
            sx={{ width: '100%' }}
          >
            <IdChip id={quote.key} />
            <StatusPill status={quote.status} short />
            <Grid
              sx={{
                flex: '1 1 auto',
              }}
            >
              {quoteItem.type === QuoteItemType.Hourly &&
                quoteItem.hourlyRate != undefined && (
                  <Grid
                    sx={{
                      display: 'flex',
                      flexDirection: {
                        xs: 'column!important',
                        sm: 'column!important',
                        md: 'row!important',
                      },
                      textAlign: { xs: 'left', sm: 'center' },
                      justifyContent: 'space-around',
                      flex: '1 1 auto',
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        marginLeft: '0.4rem',
                        textTransform: 'none',
                        lineHeight: '1.2',
                        fontSize: {
                          lg: '16px',
                          md: '14px',
                          sm: '12px',
                          xs: '15px!important',
                        },
                      }}
                      data-testid="quoteType"
                    >
                      Hourly Fee:{' '}
                      <span data-testid="typeTotal">
                        ${formattedAmount(toDollars(quoteItem.hourlyRate))}
                      </span>
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        marginLeft: '0.4rem',
                        textTransform: 'none',
                        lineHeight: '1.2',
                        fontSize: {
                          lg: '16px',
                          md: '14px',
                          sm: '12px',
                          xs: '15px!important',
                        },
                      }}
                      data-testid="quoteType"
                    >
                      Fee Deposit/Security Retainer:{' '}
                      <span data-testid="typeTotal">
                        ${formattedAmount(finalTotal)}
                      </span>
                    </Typography>
                  </Grid>
                )}
              {quoteItem.type === QuoteItemType.Flat && (
                <>
                  <Typography
                    variant="h4"
                    sx={{
                      marginLeft: '0.4rem',
                      textTransform: 'none',
                      lineHeight: '1.2',
                      fontSize: {
                        lg: '16px',
                        md: '14px',
                        sm: '12px',
                        xs: '15px!important',
                      },
                    }}
                    data-testid="quoteType"
                  >
                    Flat/Fixed Fee:{' '}
                    <span data-testid="typeTotal">
                      ${formattedAmount(finalTotal)}
                    </span>
                  </Typography>
                </>
              )}
            </Grid>
          </Button>
        }
        onClickMenu={onClickMenu}
        disableMenu={false}
        subtitle={
          formattedUpdateTime != null
            ? `Created: ${formattedUpdateTime}`
            : undefined
        }
      />

      {expanded && (
        <>
          <Divider sx={{ mt: 3 }} />
          <QuoteStatusMessage status={quote.status} />
        </>
      )}

      {expanded && quoteItem.type == QuoteItemType.Hourly && (
        <HourlyFeeQuote
          {...quoteItem}
          details={quote.details}
          payableAmount={toDollars(quoteItem.payableAmount)}
          discount={quote.items[0].discount ? quote.items[0].discount : 0}
          discountTotal={discountInUSD}
          hourlyRate={
            quoteItem?.hourlyRate ? toDollars(quoteItem?.hourlyRate) : 0
          }
          subtotal={
            quoteItem?.hourlyRate
              ? toDollars(quoteItem?.hourlyRate) - discountInUSD
              : 0
          }
          total={finalTotal}
          sx={{ mt: 2 }}
        />
      )}
      {expanded && quoteItem.type == QuoteItemType.Flat && (
        <FlatFeeQuote
          {...quoteItem}
          details={quote.details}
          payableAmount={toDollars(quoteItem.payableAmount)}
          discount={quote.items[0].discount ? quote.items[0].discount : 0}
          discountTotal={discountInUSD}
          filingAndOtherFees={
            quoteItem?.filingAndOtherFees
              ? toDollars(quoteItem?.filingAndOtherFees)
              : 0
          }
          subtotal={toDollars(quoteItem.payableAmount) - discountInUSD}
          total={finalTotal}
          sx={{ mt: 2 }}
        />
      )}
      {expanded && !readOnly && actions}
    </ItemBoundingBox>
  )
}

export default QuoteListItem
